type IntervalMetadata = {
	starttime: number
}

const manager = new class IntervalManager {
	private readonly intervals = new Map<Interval, IntervalMetadata>()
	private nextFrame: number = 0
	private lastTimestamp = 0

	public add(interval: Interval) {
		this.intervals.set(interval, {
			starttime: performance.now() + interval.delay,
		})

		if (this.intervals.size == 1) {
			this.start()
		}
	}

	public remove(interval: Interval) {
		this.intervals.delete(interval)

		if (this.intervals.size == 0) {
			this.stop()
		}
	}

	public has(interval: Interval) {
		return this.intervals.has(interval)
	}

	private start() {
		this.lastTimestamp = performance.now()
		this.tick()
	}

	private stop() {
		cancelAnimationFrame(this.nextFrame)
		this.nextFrame = null
	}

	private tick = (timestamp: number = 0) => {
		if (this.intervals.size != 0) {
			this.nextFrame = requestAnimationFrame(this.tick)
		}

		const delta = Math.max(timestamp - this.lastTimestamp, 0.0001)

		for (const [interval, metadata] of this.intervals) {
			const offset = timestamp - metadata.starttime
			let pos = Math.min(offset / interval.duration, 1)

			if (offset < 0 && timestamp > (metadata.starttime - delta)) {
				pos = 0
			}
			else if (offset < 0) {
				continue
			}

			interval.fn?.(pos)

			if (pos >= 1) {
				if (interval.loop) {
					metadata.starttime = performance.now()
				}
				else {
					this.intervals.delete(interval)
				}
			}
		}

		this.lastTimestamp = timestamp
	}
}

// Interval options.
export type IntervalFn = (pos: number) => void

export interface IntervalOptions {
	autostart?: boolean
	delay?: number
	duration?: number
	loop?: boolean
}

/**
 * Interval class utilizing `requestAnimationFrame`.
 */
export default class Interval {
	public delay: number
	public duration: number
	public loop: boolean

	public get isRunning(): boolean {
		return manager.has(this)
	}

	public constructor(
		options: IntervalOptions = {}, 
		public fn?: IntervalFn
	) {
		this.delay = options.delay || 0
		this.duration = options.duration || 1000
		this.loop = options.loop || false

		if (options.autostart) {
			this.start()
		}
	}

	public start() {
		manager.add(this)
	}

	public stop() {
		manager.remove(this)
	}
}